import {
  createSlice,
  createAsyncThunk,
  createSelector,
  PayloadAction,
} from "@reduxjs/toolkit";
import { toast } from "sonner";
import { AxiosError } from "axios";
import {
  Task,
  TaskQueryParams,
  FetchTasksResponse,
  TaskState,
  OfflineQueueItem,
  SubmissionDetails,
} from "./taskTypes";
import apiService from "../../services/api.services";

// Grade data interface

interface GradeData {
  grade: number;
  feedback: string;
}

// Constants
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second
const OFFLINE_STORAGE_KEY = "task_offline_data";

// Helper functions
const createCacheKey = (params: TaskQueryParams): string => {
  return Object.entries(params)
    .filter(([_, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
};

const saveToOfflineStorage = (data: Record<number, Task>): void => {
  try {
    localStorage.setItem(OFFLINE_STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save to offline storage:", error);
  }
};

const loadFromOfflineStorage = (): Record<number, Task> | null => {
  try {
    const data = localStorage.getItem(OFFLINE_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error("Failed to load from offline storage:", error);
    return null;
  }
};

// Initial state
const initialState: TaskState = {
  tasks: {},
  status: "idle",
  fetchTaskComplete: false,
  error: null,
  total: 0,
  currentPage: 1,
  lastFetched: {},
  pendingUpdates: {},
  offlineQueue: [],
  activeRequests: {},
  retryCount: {},
  submissionDetails: {
    submissions: {},
    taskInfo: null,
    status: "idle",
    error: null,
    total: 0,
    currentPage: 1,
    currentSubmission: null, // Add this line
  },
};

// Thunks
export const fetchTasks = createAsyncThunk<
  { tasks: Task[]; pagination: any; cacheKey: string } | null,
  TaskQueryParams,
  { state: { tasks: TaskState }; rejectValue: { message: string } }
>(
  "tasks/fetchTasks",
  async (params, { getState, rejectWithValue, dispatch }) => {
    const state = getState();
    const cacheKey = createCacheKey(params);
    const requestId = `fetch_${cacheKey}`;

    if (state.tasks.activeRequests[requestId]) {
      return null;
    }

    if (!navigator.onLine) {
      const offlineData = loadFromOfflineStorage();
      if (offlineData) {
        return {
          tasks: Object.values(offlineData),
          pagination: {
            total: Object.keys(offlineData).length,
            currentPage: 1,
            totalPages: 1,
          },
          cacheKey,
        };
      }
      return rejectWithValue({ message: "You are offline" });
    }

    const lastFetched = state.tasks.lastFetched[cacheKey] || 0;
    if (Date.now() - lastFetched < CACHE_DURATION) {
      return null;
    }

    let retryCount = 0;
    while (retryCount < MAX_RETRIES) {
      try {
        const queryParams = new URLSearchParams({
          page: String(params.page || 1),
          limit: String(params.limit || 10),
          ...(params.createdBy && { createdBy: String(params.createdBy) }),
          ...(params.taskType && { taskType: params.taskType }),
        });

        dispatch(setActiveRequest(requestId));
        const response = await apiService.get<FetchTasksResponse>(
          `/tasks?${queryParams}`,
        );

        const normalizedData =
          response.tasks?.reduce(
            (acc, task) => ({
              ...acc,
              [task.id]: task,
            }),
            {}
          ) || {};

        saveToOfflineStorage(normalizedData);

        return { ...response, cacheKey };
      } catch (error) {
        retryCount++;
        if (retryCount === MAX_RETRIES) {
          const axiosError = error as AxiosError<{ message: string }>;
          toast.error(
            axiosError.response?.data?.message || "Failed to fetch tasks"
          );
          return rejectWithValue({
            message:
              axiosError.response?.data?.message || "Failed to fetch tasks",
          });
        }
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * retryCount)
        );
      } finally {
        dispatch(removeActiveRequest(requestId));
      }
    }

    return rejectWithValue({ message: "Max retries exceeded" });
  }
);

export const createTask = createAsyncThunk<
  Task,
  FormData,
  { rejectValue: { message: string } }
>("tasks/createTask", async (formData, { rejectWithValue }) => {
  try {
    const response = await apiService.post<Task>(
      `/tasks/create-task`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    toast.success("Task created successfully");
    return response;
  } catch (error) {
    console.error(error);
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(axiosError.response?.data?.message || "Failed to create task");
    return rejectWithValue({
      message: axiosError.response?.data?.message || "Failed to create task",
    });
  }
});

export const updateTask = createAsyncThunk<
  { id: number; updates: Task; optimisticId: string; isOffline?: boolean },
  { id: number; updates: Partial<Task> },
  { rejectValue: { error: any; optimisticId: string; id: number } }
>(
  "tasks/updateTask",
  async ({ id, updates }, { dispatch, rejectWithValue }) => {
    const optimisticId = `update_${id}_${Date.now()}`;

    if (!navigator.onLine) {
      dispatch(
        addToOfflineQueue({
          type: "UPDATE",
          payload: { id, updates },
          timestamp: Date.now(),
        })
      );
      return { id, updates: updates as Task, optimisticId, isOffline: true };
    }

    try {
      const response = await apiService.put<Task>(
        `/tasks/${id}`,
        updates,
      );
      toast.success("Task updated successfully");
      return { id, updates: response, optimisticId };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to update task"
      );
      return rejectWithValue({
        error: axiosError.response?.data,
        optimisticId,
        id,
      });
    }
  }
);

export const deleteTask = createAsyncThunk(
  "tasks/deleteTask",
  async (id: number, { rejectWithValue }) => {
    try {
      await apiService.delete(`/tasks/${id}`);
      toast.success("Task deleted successfully");
      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to delete task"
      );
      return rejectWithValue({
        message: axiosError.response?.data?.message || "Failed to delete task",
      });
    }
  }
);

export const submitTask = createAsyncThunk<
  Task,
  { taskId: number; formData: FormData },
  { rejectValue: { message: string } }
>("tasks/submitTask", async ({ taskId, formData }, { rejectWithValue }) => {
  try {
    const response = await apiService.post<Task>(
      `/tasks/${taskId}/submit`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    toast.success("Task submitted successfully");
    return response;
  } catch (error) {
    console.error(error);
    const axiosError = error as AxiosError<{ message: string }>;
    toast.error(axiosError.response?.data?.message || "Failed to submit task");
    return rejectWithValue({
      message: axiosError.response?.data?.message || "Failed to submit task",
    });
  }
});

export const fetchDetailedsubmissions = createAsyncThunk<
  {
    submissions: SubmissionDetails[];
    taskInfo: {
      totalSubmissions: number;
      pendingGrading: number;
      averageGrade?: number;
    };
    total: number;
    currentPage: number;
  },
  {
    taskId: number;
    page?: number;
    limit?: number;
    sortBy?: "studentName" | "submittedAt" | "status" | "grade";
    sortDirection?: "asc" | "desc";
    status?: string;
  },
  { rejectValue: { message: string } }
>(
  "tasks/fetchdetailedsubmissions",
  async (
    { taskId, page = 1, limit = 10, sortBy, sortDirection, status },
    { rejectWithValue }
  ) => {
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(sortBy && { sortBy }),
        ...(sortDirection && { sortDirection }),
        ...(status && { status }),
      });

      const response = await apiService.get<{ submissions: SubmissionDetails[]; taskInfo: any; pagination: { total: number; page: number } }>(
        `/tasks/${taskId}/submissions/detailed?${queryParams}`,
      );

      return {
        submissions: response.submissions,
        taskInfo: response.taskInfo,
        total: response.pagination.total,
        currentPage: response.pagination.page,
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message ||
        "Failed to fetch submission details"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message ||
          "Failed to fetch submission details",
      });
    }
  }
);

export const fetchSubmissions = createAsyncThunk<
  SubmissionDetails,
  { taskId: number; studentId: number },
  { rejectValue: { message: string } }
>(
  "tasks/fetchSubmission",
  async ({ taskId, studentId }, { rejectWithValue }): Promise<SubmissionDetails | ReturnType<typeof rejectWithValue>> => {
    try {
      const response = await apiService.get<SubmissionDetails>(
        `/tasks/${taskId}/submission/${studentId}`,
      );

      return response;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message ||
        "Failed to fetch submission details"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message ||
          "Failed to fetch submission details",
      });
    }
  }
);

export const gradeSubmission = createAsyncThunk<
  Task,
  { submissionId: number; gradeData: GradeData },
  { rejectValue: { message: string } }
>(
  "tasks/gradeTask",
  async ({ submissionId, gradeData }, { rejectWithValue }) => {
    try {
      const response = await apiService.patch(
        `/tasks/${submissionId}/grade`,
        gradeData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      toast.success("Task graded successfully");
      return (response as { data: Task }).data;
    } catch (error) {
      console.error(error);
      const axiosError = error as AxiosError<{ message: string }>;
      toast.error(
        axiosError.response?.data?.message || "Failed to grade submission"
      );
      return rejectWithValue({
        message:
          axiosError.response?.data?.message || "Failed to grade submission",
      });
    }
  }
);

export const processOfflineQueue = createAsyncThunk(
  "tasks/processOfflineQueue",
  async (_, { getState, dispatch }) => {
    const state = getState() as { tasks: TaskState };
    const queue = [...state.tasks.offlineQueue];

    for (const item of queue) {
      try {
        switch (item.type) {
          case "UPDATE":
            await dispatch(updateTask(item.payload)).unwrap();
            break;
          case "DELETE":
            await dispatch(deleteTask(item.payload)).unwrap();
            break;
        }
        dispatch(removeFromOfflineQueue(item));
      } catch (error) {
        console.error("Failed to process offline item:", error);
      }
    }
  }
);

// Slice
const taskSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    resetSubmissionDetails: (state) => {
      state.submissionDetails = initialState.submissionDetails;
    },
    resetCurrentSubmission: (state) => {
      state.submissionDetails.currentSubmission = null;
    },
    setSubmissionsPage: (state, action: PayloadAction<number>) => {
      state.submissionDetails.currentPage = action.payload;
    },

    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    resetTaskError: (state) => {
      state.error = null;
    },
    resetTaskStatus: (state) => {
      state.status = 'idle';
    },
    invalidateCache: (state) => {
      state.lastFetched = {};
    },
    setActiveRequest: (state, action: PayloadAction<string>) => {
      state.activeRequests[action.payload] = true;
    },
    removeActiveRequest: (state, action: PayloadAction<string>) => {
      delete state.activeRequests[action.payload];
    },
    addToOfflineQueue: (state, action: PayloadAction<OfflineQueueItem>) => {
      state.offlineQueue.push(action.payload);
      saveToOfflineStorage(state.tasks);
    },
    removeFromOfflineQueue: (
      state,
      action: PayloadAction<OfflineQueueItem>
    ) => {
      state.offlineQueue = state.offlineQueue.filter(
        (item) => item.timestamp !== action.payload.timestamp
      );
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch tasks cases
      .addCase(fetchTasks.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        if (action.payload) {
          const { tasks, pagination, cacheKey } = action.payload;
          state.tasks = tasks.reduce(
            (acc, task) => ({
              ...acc,
              [task.id]: task,
            }),
            {}
          );
          state.total = pagination.total;
          state.currentPage = pagination.currentPage;
          state.lastFetched[cacheKey] = Date.now();
          state.status = "succeeded";
          state.fetchTaskComplete = true
        }
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to fetch tasks";
      })

      // Create task cases
      .addCase(createTask.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.tasks[action.payload.id] = action.payload;
        state.status = "succeeded";
      })

      .addCase(createTask.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to create tasks";
      })

      // Update task
      .addCase(updateTask.fulfilled, (state, action) => {
        if (!action.payload.isOffline) {
          state.tasks[action.payload.id] = action.payload.updates;
        }
      })

      // Delete task case
      .addCase(deleteTask.fulfilled, (state, action) => {
        delete state.tasks[action.payload];
      })

      // Process offline queue
      .addCase(processOfflineQueue.fulfilled, (state) => {
        state.offlineQueue = [];
      })

      // Submit task cases
      .addCase(submitTask.pending, (state, action) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(submitTask.fulfilled, (state, action) => {
        state.tasks[action.payload.id] = action.payload;
        state.status = "succeeded";
      })

      .addCase(submitTask.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload?.message || "Failed to create tasks";
      })

      // fetchDetailedSubmissions cases
      .addCase(fetchDetailedsubmissions.pending, (state) => {
        state.submissionDetails.status = "loading";
        state.submissionDetails.error = null;
      })
      .addCase(fetchDetailedsubmissions.fulfilled, (state, action) => {
        state.submissionDetails.submissions = action.payload.submissions.reduce(
          (acc, submission) => ({
            ...acc,
            [submission.id]: submission,
          }),
          {}
        );
        state.submissionDetails.taskInfo = action.payload.taskInfo;
        state.submissionDetails.total = action.payload.total;
        state.submissionDetails.currentPage = action.payload.currentPage;
        state.submissionDetails.status = "succeeded";
      })
      .addCase(fetchDetailedsubmissions.rejected, (state, action) => {
        state.submissionDetails.status = "failed";
        state.submissionDetails.error =
          action.payload?.message || "Failed to fetch submission details";
      })

      //fetchSubmissions cases
      .addCase(fetchSubmissions.pending, (state) => {
        state.submissionDetails.status = "loading";
        state.submissionDetails.error = null;
      })
      .addCase(fetchSubmissions.fulfilled, (state, action) => {
        state.submissionDetails.currentSubmission = action.payload;
        state.submissionDetails.status = "succeeded";
      })
      .addCase(fetchSubmissions.rejected, (state, action) => {
        state.submissionDetails.status = "failed";
        state.submissionDetails.error =
          action.payload?.message || "Failed to fetch submission details";
      })
      // Grade submission cases
      .addCase(gradeSubmission.pending, (state, action) => {
        state.submissionDetails.status = "loading";
        state.error = null;
      })
      .addCase(gradeSubmission.fulfilled, (state, action) => {
        state.submissionDetails.status = "succeeded";
      })

      .addCase(gradeSubmission.rejected, (state, action) => {
        state.submissionDetails.status = "failed";
        state.error = action.payload?.message || "Failed to grade submission";
      });
  },
});

// Actions
export const {
  setCurrentPage,
  invalidateCache,
  setActiveRequest,
  removeActiveRequest,
  addToOfflineQueue,
  removeFromOfflineQueue,
  resetTaskStatus,
} = taskSlice.actions;

// Selectors
export const selectTasks = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.tasks],
  (tasks): Task[] => Object.values(tasks)
);
export const selectTaskFetchComplete = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.fetchTaskComplete],
  (fetchTaskComplete) => fetchTaskComplete
);

export const selectTaskById = createSelector(
  [
    (state: { tasks: TaskState }) => state.tasks.tasks,
    (_: any, id: number) => id,
  ],
  (tasks, id): Task | undefined => tasks[id]
);

export const selectSubmissionDetails = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.submissions],
  (submissions): SubmissionDetails[] => Object.values(submissions)
);

export const selectSubmissionById = createSelector(
  [
    (state: { tasks: TaskState }) => state.tasks.submissionDetails.submissions,
    (_: any, id: number) => id,
  ],
  (submissions, id): SubmissionDetails | undefined => submissions[id]
);

export const selectTaskSubmissionInfo = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.taskInfo],
  (taskInfo) => taskInfo
);

export const selectSubmissionStatus = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.status],
  (status) => status
);

export const selectSubmissionError = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.error],
  (error) => error
);

export const selectSubmissionPagination = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails],
  (submissionDetails) => ({
    currentPage: submissionDetails.currentPage,
    total: submissionDetails.total,
  })
);

export const selectCurrentSubmission = createSelector(
  [
    (state: { tasks: TaskState }) =>
      state.tasks.submissionDetails.currentSubmission,
  ],
  (currentSubmission) => currentSubmission
);

export const selectDetailedTaskStatistics = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.tasks],
  (
    tasks
  ): {
    total: number;
    statusCounts: {
      Submitted: number;
      Pending: number;
      Late: number;
      Graded: number;
    };
    percentages: {
      submittedPercent: number;
      pendingPercent: number;
      latePercent: number;
      gradedPercent: number;
    };
  } => {
    const taskArray = Object.values(tasks);
    const total = taskArray.length;

    // Initialize status counts
    const statusCounts = {
      Submitted: 0,
      Pending: 0,
      Late: 0,
      Graded: 0,
    };

    // Count tasks by status
    taskArray.forEach((task) => {
      if (task.submissionStatus) {
        statusCounts[task.submissionStatus]++;
      }

      if (task.submissionStatus === "Submitted" || task.submissionStatus === "Graded") {
        statusCounts.Submitted++;
      }

      if (task.submissionStatus === "Late" || task.submissionStatus === "Pending") {
        statusCounts.Pending++;
      }
    });

    // Calculate percentages
    const percentages = {
      submittedPercent: total > 0 ? (statusCounts.Submitted / total) * 100 : 0,
      pendingPercent: total > 0 ? (statusCounts.Pending / total) * 100 : 0,
      latePercent: total > 0 ? (statusCounts.Late / total) * 100 : 0,
      gradedPercent: total > 0 ? (statusCounts.Graded / total) * 100 : 0,
    };

    return {
      total,
      statusCounts,
      percentages,
    };
  }
);

// Basic Task Statistics Selectors
export const selectTaskStatistics = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.tasks],
  (
    tasks
  ): {
    total: number;
    submitted: number;
    pending: number;
    graded: number;
  } => {
    const taskArray = Object.values(tasks);
    return {
      total: taskArray.length,
      submitted: taskArray.filter(
        (task) => task.submissionStatus === "Submitted"
      ).length,
      pending: taskArray.filter((task) => task.submissionStatus === "Pending")
        .length,
      graded: taskArray.filter((task) => task.submissionStatus === "Graded")
        .length,
    };
  }
);

export const selectTotalSubmissionCount = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.tasks],
  (tasks): number => {
    return Object.values(tasks).reduce((total, task) => {
      return total + (task.submissionCount || 0);
    }, 0);
  }
);

// Detailed Submission Statistics Selectors
export const selectSubmissionStatistics = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.submissions],
  (
    submissions
  ): {
    totalSubmissions: number;
    gradedSubmissions: number;
    pendingSubmissions: number;
    lateSubmissions: number;
    averageGrade: number;
    gradeDistribution: { grade: number; count: number }[];
  } => {
    const submissionArray = Object.values(submissions);

    // Initialize statistics
    const stats = {
      totalSubmissions: submissionArray.length,
      gradedSubmissions: 0,
      pendingSubmissions: 0,
      lateSubmissions: 0,
      averageGrade: 0,
      gradeDistribution: [] as { grade: number; count: number }[],
    };

    // Grade tracking for distribution
    const grades: number[] = [];
    let totalGrade = 0;
    let gradedCount = 0;

    // Process each submission
    submissionArray.forEach((submission) => {
      // Count by status
      if (submission.status === "Graded") {
        stats.gradedSubmissions++;
        if (submission.gradeInfo?.grade !== undefined) {
          grades.push(submission.gradeInfo.grade);
          totalGrade += submission.gradeInfo.grade;
          gradedCount++;
        }
      } else if (submission.status === "Pending") {
        stats.pendingSubmissions++;
      }

      if (submission.status === "Late") {
        stats.lateSubmissions++;
      }
    });

    // Calculate average grade
    stats.averageGrade = gradedCount > 0 ? totalGrade / gradedCount : 0;

    // Calculate grade distribution
    const gradeRanges = [0, 60, 70, 80, 90, 100];
    stats.gradeDistribution = gradeRanges.slice(0, -1).map((min, index) => {
      const max = gradeRanges[index + 1];
      return {
        grade: min,
        count: grades.filter((grade) => grade >= min && grade < max).length,
      };
    });

    return stats;
  }
);

// Selector for Individual Submission Grades
export const selectSubmissionGrades = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.submissions],
  (
    submissions
  ): {
    studentId: number;
    studentName: string;
    grade: number;
    feedback?: string;
    submittedAt: Date;
    gradedAt?: Date;
  }[] => {
    return Object.values(submissions)
      .filter(
        (submission) => submission.status === "Graded" && submission.gradeInfo
      )
      .map((submission) => ({
        studentId: submission.student.id,
        studentName: submission.student.name,
        grade: submission.gradeInfo?.grade || 0,
        feedback: submission.gradeInfo?.feedback,
        submittedAt: new Date(submission.submittedAt),
        gradedAt: submission.gradeInfo?.gradedAt
          ? new Date(submission.gradeInfo.gradedAt)
          : undefined,
      }));
  }
);

// Selector for Summary Statistics
export const selectTaskSummaryStats = createSelector(
  [
    (state: { tasks: TaskState }) => state.tasks.tasks,
    (state: { tasks: TaskState }) => state.tasks.submissionDetails.submissions,
  ],
  (
    tasks,
    submissions
  ): {
    taskCount: number;
    submissionRate: number;
    completionRate: number;
    averageGrade: number;
  } => {
    const taskArray = Object.values(tasks);
    const submissionArray = Object.values(submissions);

    const taskCount = taskArray.length;
    const totalPossibleSubmissions = taskArray.reduce(
      (sum, task) => sum + (task.enrolledCount || 0),
      0
    );
    const actualSubmissions = submissionArray.length;
    const gradedSubmissions = submissionArray.filter(
      (sub) => sub.status === "Graded"
    ).length;

    let totalGrade = 0;
    let gradedCount = 0;

    submissionArray.forEach((submission) => {
      if (submission.gradeInfo?.grade !== undefined) {
        totalGrade += submission.gradeInfo.grade;
        gradedCount++;
      }
    });

    return {
      taskCount,
      submissionRate:
        totalPossibleSubmissions > 0
          ? (actualSubmissions / totalPossibleSubmissions) * 100
          : 0,
      completionRate:
        actualSubmissions > 0
          ? (gradedSubmissions / actualSubmissions) * 100
          : 0,
      averageGrade: gradedCount > 0 ? totalGrade / gradedCount : 0,
    };
  }
);

// Enhanced Submission Statistics Selector
export const selectDetailedSubmissionStatistics = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.submissionDetails.submissions],
  (
    submissions
  ): {
    totalSubmissions: number;
    statusCounts: {
      graded: number;
      pending: number;
      late: number;
      onTime: number;
    };
    percentages: {
      gradedPercent: number;
      pendingPercent: number;
      latePercent: number;
      onTimePercent: number;
    };
    gradeStats: {
      averageGrade: number;
      highestGrade: number;
      lowestGrade: number;
      medianGrade: number;
    };
    timelinessStats: {
      averageSubmissionDelay: number;
      lateSubmissionCount: number;
    };
  } => {
    const submissionArray = Object.values(submissions);
    const total = submissionArray.length;

    // Initialize counters
    const statusCounts = {
      graded: 0,
      pending: 0,
      late: 0,
      onTime: 0,
    };

    const grades: number[] = [];
    const taskGrades: { [taskId: number]: number[] } = {};
    let totalTaskGrade = 0;
    let totalTaskCount = 0;
    let totalDelay = 0;
    let lateCount = 0;

    // Process each submission
    submissionArray.forEach((submission) => {
      // Count by status
      if (submission.status === "Graded") {
        statusCounts.graded++;
        if (submission.gradeInfo?.grade !== undefined) {
          // Collect grades for each task
          if (!taskGrades[submission.task.id]) {
            taskGrades[submission.task.id] = [];
          }
          taskGrades[submission.task.id].push(submission.gradeInfo.grade);
        }
      }
      if (submission.status === "Pending") {
        statusCounts.pending++;
      }
      if (submission.status === "Late") {
        statusCounts.late++;
        lateCount++;

        // Calculate submission delay
        const dueDate = new Date(submission.task.dueDate);
        const submittedDate = new Date(submission.submittedAt);
        const delay = submittedDate.getTime() - dueDate.getTime();
        if (delay > 0) {
          totalDelay += delay / (1000 * 60 * 60 * 24); // Convert to days
        }
      }
      if (submission.status === "Submitted") {
        statusCounts.onTime++;
      }
    });

    // Calculate average grade for each task and aggregate
    Object.entries(taskGrades).forEach(([taskId, taskSpecificGrades]) => {
      if (taskSpecificGrades.length > 0) {
        const taskAverage =
          taskSpecificGrades.reduce((sum, grade) => sum + grade, 0) /
          taskSpecificGrades.length;
        grades.push(taskAverage);
        totalTaskGrade += taskAverage;
        totalTaskCount++;
      }
    });

    // Calculate percentages
    const percentages = {
      gradedPercent: total > 0 ? (statusCounts.graded / total) * 100 : 0,
      pendingPercent: total > 0 ? (statusCounts.pending / total) * 100 : 0,
      latePercent: total > 0 ? (statusCounts.late / total) * 100 : 0,
      onTimePercent: total > 0 ? (statusCounts.onTime / total) * 100 : 0,
    };

    // Calculate grade statistics
    grades.sort((a, b) => a - b);
    const gradeStats = {
      averageGrade: totalTaskCount > 0 ? totalTaskGrade / totalTaskCount : 0,
      highestGrade: grades.length > 0 ? Math.max(...grades) : 0,
      lowestGrade: grades.length > 0 ? Math.min(...grades) : 0,
      medianGrade:
        grades.length > 0
          ? grades.length % 2 === 0
            ? (grades[grades.length / 2 - 1] + grades[grades.length / 2]) / 2
            : grades[Math.floor(grades.length / 2)]
          : 0,
    };

    // Calculate timeliness statistics
    const timelinessStats = {
      averageSubmissionDelay: lateCount > 0 ? totalDelay / lateCount : 0,
      lateSubmissionCount: lateCount,
    };

    return {
      totalSubmissions: total,
      statusCounts,
      percentages,
      gradeStats,
      timelinessStats,
    };
  }
);

// Task Timeline Statistics Selector
export const selectTaskTimelineStatistics = createSelector(
  [(state: { tasks: TaskState }) => state.tasks.tasks],
  (
    tasks
  ): {
    upcomingTasks: number;
    overdueTasks: number;
    completedTasks: number;
    tasksNeedingAttention: Task[];
  } => {
    const taskArray = Object.values(tasks);
    const now = new Date();

    const stats = {
      upcomingTasks: 0,
      overdueTasks: 0,
      completedTasks: 0,
      tasksNeedingAttention: [] as Task[],
    };

    taskArray.forEach((task) => {
      const endDate = new Date(task.endDate);

      if (endDate > now && task.submissionStatus !== "Graded") {
        stats.upcomingTasks++;
      }

      if (endDate < now && task.submissionStatus !== "Graded") {
        stats.overdueTasks++;
        stats.tasksNeedingAttention.push(task);
      }

      if (task.submissionStatus === "Graded") {
        stats.completedTasks++;
      }
    });

    return stats;
  }
);
// Network listeners
export const setupNetworkListeners = (store: any) => {
  window.addEventListener("online", () => {
    store.dispatch(processOfflineQueue());
  });

  window.addEventListener("offline", () => {
    toast.warn("You are offline. Changes will be saved locally.");
  });
};

export const {
  resetTaskError,
  resetSubmissionDetails,
  setSubmissionsPage,
  resetCurrentSubmission,
} = taskSlice.actions;
export default taskSlice.reducer;
